import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import SeoSocialTags from '../components/SeoSocialTags';
import SolutionChildHero, { SOLUTION_CHILD_HERO_ILLUSTRATIONS } from '../components/SolutionChildHero';
import './SolutionChildPage.css';

const CANONICAL_URL = 'https://circumvent.io/solutions/context';
const PAGE_TITLE = 'Application and owner context | Circumvent';
const PAGE_DESCRIPTION =
  'Map Applications, Services and owners so validated Cloud and AppSec findings reach the right team.';
const NEXT_STEPS_BACKGROUND = `${process.env.PUBLIC_URL}/images/backgrounds/gradient-multi-bg.webp`;
const NEXT_STEPS_SECTION_STYLE = {
  '--cta-bg-image': `url(${NEXT_STEPS_BACKGROUND})`,
};
const APPLICATION_CONTEXT_MAP_ILLUSTRATION = '/images/features/icon_Application-context-map.webp';
const APPLICATION_CONTEXT_MAP_ILLUSTRATION_2X =
  '/images/features/icon_Application-context-map@2x.webp';
const SERVICE_GRAPH_ILLUSTRATION = '/images/features/icon_Service-graph.webp';
const SERVICE_GRAPH_ILLUSTRATION_2X = '/images/features/icon_Service-graph@2x.webp';

const DISCOVERY_DIAGRAM_SRC_SET = (standard, retina) => `${standard} 643w, ${retina} 1286w`;
const DISCOVERY_DIAGRAM_SIZES = '(max-width: 768px) 320px, min(66vw, 900px)';
const DISCOVERY_SERVICE_GRAPH_SIZES = '(max-width: 768px) 440px, min(66vw, 900px)';
const CONTEXT_GRAPH_VISUALIZE_ILLUSTRATION = '/images/features/Business-Context-Visualize.svg';
const CONTEXT_GRAPH_IDENTIFY_ILLUSTRATION = '/images/features/Business-Context-Identify.svg';
const CONTEXT_GRAPH_DISCOVER_ILLUSTRATION = '/images/features/Business-Context-Discover.svg';

/* Numbered stepper tabs swap the framed graph view and its caption */
const STAKEHOLDERS_GRAPH_STEPS = [
  {
    id: 'visualize',
    label: 'Visualize',
    title: 'Visualize:',
    body: 'Resource Relationships and Dependencies',
    imageSrc: CONTEXT_GRAPH_VISUALIZE_ILLUSTRATION,
  },
  {
    id: 'identify',
    label: 'Identify',
    title: 'Identify:',
    body: 'Resources with Risks Detected Against Them',
    imageSrc: CONTEXT_GRAPH_IDENTIFY_ILLUSTRATION,
  },
  {
    id: 'discover',
    label: 'Discover',
    title: 'Discover:',
    body: 'Exposed Sensitive Data with Attack Paths',
    imageSrc: CONTEXT_GRAPH_DISCOVER_ILLUSTRATION,
  },
];

const APPLICATION_DETAIL_ITEMS = [
  {
    id: 'smart-discovery',
    title: 'Smart Discovery',
    body:
      'Agentic discovery draws on the tenant profile, public information, and domain context to distill suggested Applications.',
  },
  {
    id: 'contextual-attributes',
    title: 'Contextual Attributes',
    body:
      'Application attributes include business criticality, data confidentiality, compliance standards, and ownership.',
  },
  {
    id: 'risk-governance',
    title: 'Risk Governance',
    body:
      'Application context is inherited by security Insights and supports a risk narrative for executive leaders, as well as appropriate governance.',
  },
];

const SERVICE_DETAIL_ITEMS = [
  {
    id: 'service-mapping',
    title: 'Service Mapping',
    body:
      'Services are created as a living graph with clear technical boundaries. They are brought to life as a unit with relationships and dependencies, instead of a flat list of accounts and resources.',
  },
  {
    id: 'ownership-context',
    title: 'Ownership Context',
    body:
      'Services have explicit ownership. They are mapped to Applications for contextual risk analysis, prioritization, and remediation escalations.',
  },
  {
    id: 'adaptive-discovery',
    title: 'Adaptive Discovery',
    body:
      'They are inferred agentically using tags, names, dependencies, and relationships, and refreshed as cloud, code, and identity systems change.',
  },
];

const DiscoveryDetailList = ({ items }) => (
  <div className="solution-child-page__discovery-detail">
    {items.map((item) => (
      <div key={item.id} className="solution-child-page__discovery-detail-item">
        <h4 className="solution-child-page__discovery-detail-title">{item.title}</h4>
        <p className="solution-child-page__discovery-detail-body">{item.body}</p>
      </div>
    ))}
  </div>
);

const PERSONA_TILES = [
  {
    id: 'security',
    title: 'Security leadership',
    body:
      'Tailor risk narrative for both executive leaders and engineering. Create program credibility with a grounding in Applications the business recognizes, and support triage SLAs with greater contextual accuracy.',
  },
  {
    id: 'platform',
    title: 'Platform & cloud',
    body:
      'Clarify Service boundaries and shared foundations for platform and cloud. Ground day-to-day work in Applications the business recognizes, with a single operational view instead of another siloed console.',
  },
  {
    id: 'engineering',
    title: 'Engineering leadership',
    body:
      'Reduce churn from mis-scoped escalations and tickets that miss real ownership. Align triage, severity, and routing with the Services and Applications your teams ship and run.',
  },
];

const STAKEHOLDER_ICONS = {
  security: (
    <svg width="46" height="46" viewBox="0 0 48 48" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M24 4.5 39 9.9v11.8c0 9.7-6.4 16.8-15 19.8-8.6-3-15-10.1-15-19.8V9.9z"
        stroke="#00202b"
        strokeWidth="2.4"
        strokeLinejoin="round"
      />
      <path
        d="M16.8 23.6 21.6 28.4 31.4 17.8"
        stroke="#35e8d6"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  platform: (
    <svg width="46" height="46" viewBox="0 0 48 48" fill="none" aria-hidden="true" focusable="false">
      <path d="M24 6 40 14 24 22 8 14z" stroke="#35e8d6" strokeWidth="2.4" strokeLinejoin="round" />
      <path
        d="M8 22 24 30 40 22"
        stroke="#00202b"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 30 24 38 40 30"
        stroke="#00202b"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  engineering: (
    <svg width="46" height="46" viewBox="0 0 48 48" fill="none" aria-hidden="true" focusable="false">
      <path
        d="M30 11C18 11 18 22 24 24c6 2 6 13-6 13"
        stroke="#00202b"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <rect x="25.5" y="6.5" width="9" height="9" rx="1.8" fill="#fff" stroke="#35e8d6" strokeWidth="2.4" />
      <rect x="13.5" y="32.5" width="9" height="9" rx="1.8" fill="#fff" stroke="#35e8d6" strokeWidth="2.4" />
    </svg>
  ),
};

const GRAPH_ROTATION_MS = 2000;

const SolutionContextPage = () => {
  const [activeGraphStepIndex, setActiveGraphStepIndex] = useState(0);
  const [pointerOverGraphSelector, setPointerOverGraphSelector] = useState(false);
  const [focusWithinGraphSelector, setFocusWithinGraphSelector] = useState(false);
  const activeGraphStep = STAKEHOLDERS_GRAPH_STEPS[activeGraphStepIndex];

  const pauseGraphAutoAdvance = pointerOverGraphSelector || focusWithinGraphSelector;

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return undefined;
    if (pauseGraphAutoAdvance) return undefined;

    const intervalId = window.setInterval(() => {
      setActiveGraphStepIndex((currentIndex) => (currentIndex + 1) % STAKEHOLDERS_GRAPH_STEPS.length);
    }, GRAPH_ROTATION_MS);

    return () => window.clearInterval(intervalId);
  }, [pauseGraphAutoAdvance]);

  const handleGraphSelectorBlurCapture = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setFocusWithinGraphSelector(false);
    }
  };

  return (
    <>
    <Helmet>
      <title>{PAGE_TITLE}</title>
      <meta name="description" content={PAGE_DESCRIPTION} />
      <link rel="canonical" href={CANONICAL_URL} />
    </Helmet>
    <SeoSocialTags
      title={PAGE_TITLE}
      description={PAGE_DESCRIPTION}
      url={CANONICAL_URL}
    />

    <main className="page page--light solution-child-page">
      <SolutionChildHero
        headingId="solution-context-hero-heading"
        eyebrow="Business Context"
        title="Discover and visualize your cloud applications"
        lead="Circumvent scans and builds a living map of your business Applications and technical Services. Security, engineering and leadership work with a shared understanding of architecture and ownership."
        illustration={SOLUTION_CHILD_HERO_ILLUSTRATIONS.context}
      />

      <section
        className="page-section page-section--white"
        aria-labelledby="solution-context-discovery-heading"
      >
        <div className="page-container page-container--stack">
          <div className="section-panel__header section-panel__header--spaced">
            <p className="section-panel__eyebrow">Discovery</p>
            <h2 id="solution-context-discovery-heading" className="section-panel__title">
              Applications &amp; Services
            </h2>
            <p className="section-panel__intro solution-child-page__section-dek">
              For organizations with multiple applications, shared platform services, and distributed
              ownership across security and engineering.
            </p>
          </div>

          <article
            className="solution-child-page__card solution-child-page__card--bleed"
            aria-labelledby="solution-context-applications-heading"
          >
            <div className="solution-child-page__card-inner solution-child-page__card-inner--discovery solution-child-page__card-inner--discovery-applications">
              <h3
                id="solution-context-applications-heading"
                className="content-block__title solution-child-page__discovery-heading"
              >
                Applications
              </h3>
              <div className="solution-child-page__card-content">
                <DiscoveryDetailList items={APPLICATION_DETAIL_ITEMS} />
              </div>
              <figure className="solution-child-page__card-media">
                <img
                  className="solution-child-page__card-img"
                  src={APPLICATION_CONTEXT_MAP_ILLUSTRATION}
                  srcSet={DISCOVERY_DIAGRAM_SRC_SET(
                    APPLICATION_CONTEXT_MAP_ILLUSTRATION,
                    APPLICATION_CONTEXT_MAP_ILLUSTRATION_2X,
                  )}
                  sizes={DISCOVERY_DIAGRAM_SIZES}
                  alt="Diagram: application with multiple services, shared cloud code and registry resources, and business unit and team ownership."
                  width={643}
                  height={405}
                  loading="lazy"
                  decoding="async"
                />
              </figure>
            </div>
          </article>

          <article
            className="solution-child-page__card solution-child-page__card--bleed"
            aria-labelledby="solution-context-services-heading"
          >
            <div className="solution-child-page__card-inner solution-child-page__card-inner--discovery solution-child-page__card-inner--discovery-services">
              <h3
                id="solution-context-services-heading"
                className="content-block__title solution-child-page__discovery-heading"
              >
                Application Services
              </h3>
              <figure className="solution-child-page__card-media">
                <img
                  className="solution-child-page__card-img solution-child-page__card-img--service-graph"
                  src={SERVICE_GRAPH_ILLUSTRATION}
                  srcSet={DISCOVERY_DIAGRAM_SRC_SET(
                    SERVICE_GRAPH_ILLUSTRATION,
                    SERVICE_GRAPH_ILLUSTRATION_2X,
                  )}
                  sizes={DISCOVERY_SERVICE_GRAPH_SIZES}
                  alt="Stylized service graph: one Service linked to network, compute, storage, serverless functions, and IAM roles and policies."
                  width={643}
                  height={406}
                  loading="lazy"
                  decoding="async"
                />
              </figure>
              <div className="solution-child-page__card-content">
                <DiscoveryDetailList items={SERVICE_DETAIL_ITEMS} />
              </div>
            </div>
          </article>
        </div>
      </section>

      <section
        className="page-section page-section--pixel-noise"
        aria-labelledby="solution-context-outcomes-heading"
      >
        <div className="page-container page-container--stack">
          <div className="section-panel__header section-panel__header--spaced solution-child-page__header--dek-to-outcomes">
            <p className="section-panel__eyebrow">Stakeholders</p>
            <h2 id="solution-context-outcomes-heading" className="section-panel__title">
              Living Architecture and Ownership
            </h2>
            <p className="section-panel__intro solution-child-page__section-dek">
              Living discovery on each scan instead of static and tribal knowledge. Triage uses Application
              and environment context, reachability, and attack paths, with evidence backed decisions on
              false-positive filtering, risk reduction, and ownership alignment so escalations stay correctly
              routed.
            </p>
          </div>
          <div
            className="solution-child-page__graph"
            onPointerEnter={() => setPointerOverGraphSelector(true)}
            onPointerLeave={() => setPointerOverGraphSelector(false)}
            onFocusCapture={() => setFocusWithinGraphSelector(true)}
            onBlurCapture={handleGraphSelectorBlurCapture}
          >
            <div
              className="solution-child-page__graph-stepper"
              role="tablist"
              aria-label="Business context graph views"
            >
              {STAKEHOLDERS_GRAPH_STEPS.map((step, index) => {
                const isActive = index === activeGraphStepIndex;
                return (
                  <button
                    key={step.id}
                    type="button"
                    role="tab"
                    id={`solution-context-graph-tab-${step.id}`}
                    aria-selected={isActive}
                    aria-controls="solution-context-graph-panel"
                    tabIndex={isActive ? 0 : -1}
                    className={`solution-child-page__graph-step${isActive ? ' is-active' : ''}`}
                    onMouseEnter={() => setActiveGraphStepIndex(index)}
                    onFocus={() => setActiveGraphStepIndex(index)}
                    onClick={() => setActiveGraphStepIndex(index)}
                  >
                    <span className="solution-child-page__graph-step-num">{index + 1}</span>
                    <span className="solution-child-page__graph-step-label">{step.label}</span>
                  </button>
                );
              })}
            </div>
            <div
              className="solution-child-page__graph-card"
              role="tabpanel"
              id="solution-context-graph-panel"
              aria-labelledby={`solution-context-graph-tab-${activeGraphStep.id}`}
            >
              <div className="solution-child-page__graph-stage">
                <img
                  className="solution-child-page__graph-img"
                  src={activeGraphStep.imageSrc}
                  alt={`${activeGraphStep.label}: ${activeGraphStep.body}`}
                  width={671}
                  height={286}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <p className="solution-child-page__graph-caption">
                <span className="solution-child-page__graph-caption-label">
                  {activeGraphStep.title}
                </span>{' '}
                {activeGraphStep.body}
              </p>
            </div>
          </div>
          <div className="solution-child-page__stakeholders">
            {PERSONA_TILES.map((persona) => (
              <div key={persona.id} className="solution-child-page__stakeholder-card">
                <span className="solution-child-page__stakeholder-icon" aria-hidden="true">
                  {STAKEHOLDER_ICONS[persona.id]}
                </span>
                <h3 className="solution-child-page__stakeholder-title">{persona.title}</h3>
                <p>{persona.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="page-section page-section--white solution-child-page__cta-section solution-child-page__cta-section--image"
        aria-labelledby="solution-context-cta-heading"
        style={NEXT_STEPS_SECTION_STYLE}
      >
        <div className="page-container page-container--stack">
          <div className="page-section__header" aria-labelledby="solution-context-cta-heading">
            <h2 id="solution-context-cta-heading">Next steps</h2>
            <p className="solution-child-page__cta-lead">
              See Circumvent in action on your environment.
            </p>
            <p className="solution-child-page__cta-actions">
              <a href="/#contact" className="btn btn-solid">
                Book a demo
              </a>
            </p>
          </div>
        </div>
      </section>
    </main>
    </>
  );
};

export default SolutionContextPage;
